import React, { useState, useEffect, useRef } from 'react';
import DailyResetTimerSkeleton from './DailyResetTimerSkeleton';
import userService from '../../services/userService';
import './DailyResetTimer.scss';

const DailyResetTimer = () => {
  const [timeUntilReset, setTimeUntilReset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const lastFetchRef = useRef(0);

  // Fetch time info from server
  const fetchTimeInfo = async () => {
    try {
      const data = await userService.getTimeInfo();
      
      // Calculate initial countdown
      const nextResetTime = new Date(data.nextResetUTC).getTime();
      const now = Date.now();
      const timeRemaining = Math.max(0, nextResetTime - now);
      
      setTimeUntilReset(timeRemaining);
      setError(null);
      setLoading(false);
      lastFetchRef.current = now;
      
    } catch (err) {
      console.error('Error fetching time info:', err);
      setError('Failed to load reset timer');
      setLoading(false);
    }
  };

  // Format time remaining into readable string
  const formatTimeRemaining = (milliseconds) => {
    if (milliseconds <= 0) {
      return 'Resetting...';
    }

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Update countdown every second
  useEffect(() => {
    if (timeUntilReset !== null) {
      intervalRef.current = setInterval(() => {
        setTimeUntilReset(prevTime => {
          const newTime = Math.max(0, prevTime - 1000);
          
          // If countdown reaches zero, re-fetch to get new reset time
          if (newTime <= 0) {
            setTimeout(fetchTimeInfo, 1000); // Small delay to let server update
          }
          
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timeUntilReset]);

  // Handle page visibility changes (laptop sleep, tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible again - re-fetch time to correct for any drift
        const timeSinceLastFetch = Date.now() - lastFetchRef.current;
        
        // Only re-fetch if it's been more than 30 seconds since last fetch
        if (timeSinceLastFetch > 30000) {
          fetchTimeInfo();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Initial fetch on component mount
  useEffect(() => {
    fetchTimeInfo();
  }, []);

  if (loading) {
    return <DailyResetTimerSkeleton />;
  }

  if (error) {
    return (
      <div className="daily-reset-timer daily-reset-timer--error">
        <span className="daily-reset-timer__text">{error}</span>
      </div>
    );
  }

  // Check if time is urgent (less than 1 minute)
  const isUrgent = timeUntilReset <= (60 * 1000);

  return (
    <div className={`daily-reset-timer ${isUrgent ? 'daily-reset-timer--urgent' : ''}`}>
      <span className="daily-reset-timer__label">Daily challenges reset in:</span>
      <span className="daily-reset-timer__countdown">{formatTimeRemaining(timeUntilReset)}</span>
    </div>
  );
};

export default DailyResetTimer;